import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Briefcase, CheckCircle2, ChevronRight, Clock, Hourglass, PlayCircle, Route } from 'lucide-react';
import { careersApi, coursesApi } from '@/api/endpoints';
import type { CareerDetailResponse, CareerStep } from '@/api/types';
import { formatTotalDuration } from '@/components/courses/duration';
import { PurchaseBox } from '@/components/courses/PurchaseBox';
import { useCourseAccess } from '@/components/courses/useCourseAccess';
import { MarkdownLite } from '@/components/ui/MarkdownLite';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { useSeo } from '@/hooks/useSeo';
import { cn } from '@/lib/cn';
import { careerSeoDescription, careerSeoTitle } from '@/lib/seoText';
import { COURSES_INDEX_PATH, careerPath, careerStructuredData, coursePath } from '@/lib/structuredData';
import { useInitialData } from '@/ssr/initialData';
import { useAuthStore } from '@/store/auth';
import { NotFound } from './NotFound';

/** Kasb sahifasi: /careers/:slug — kasb haqida va kurslar ketma-ketligi (yo'l) */
export function Career() {
  const { t } = useTranslation();
  const { slug = '' } = useParams();
  const initial = useInitialData();
  const userId = useAuthStore((s) => s.user?.id);
  const { hasCareer } = useCourseAccess();

  const [career, setCareer] = useState<CareerDetailResponse | null>(
    initial?.career?.slug === slug ? initial.career : null,
  );
  const [notFound, setNotFound] = useState(false);
  /** kurs slug → tugatilgan darslar soni (faqat kirgan foydalanuvchi, hydration'dan keyin) */
  const [done, setDone] = useState<Record<string, number>>({});

  useEffect(() => {
    if (career?.slug === slug) return;
    let cancelled = false;
    setNotFound(false);
    careersApi
      .detail(slug)
      .then((res) => {
        if (!cancelled) setCareer(res.data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const courseSlugs = (career?.steps ?? []).flatMap((s) => (s.course ? [s.course.slug] : [])).join(',');

  useEffect(() => {
    setDone({});
    if (userId === undefined || !courseSlugs) return;
    let cancelled = false;
    Promise.all(
      courseSlugs.split(',').map((cs) =>
        coursesApi
          .progress(cs)
          .then((res) => [cs, res.data.completedLessons.length] as const)
          .catch(() => [cs, 0] as const),
      ),
    ).then((rows) => {
      if (!cancelled) setDone(Object.fromEntries(rows));
    });
    return () => {
      cancelled = true;
    };
  }, [userId, courseSlugs]);

  useSeo({
    title: career ? careerSeoTitle(career.title) : 'Kasb yo\'nalishi — SQLMaster.uz',
    description: career ? careerSeoDescription(career) : undefined,
    path: careerPath(slug),
    jsonLd: career ? careerStructuredData(career) : undefined,
  });

  if (notFound) return <NotFound />;
  if (!career || career.slug !== slug) {
    return <div className="py-24 text-center text-content-secondary">{t('common.loading')}</div>;
  }

  const firstCourse = career.steps.find((s) => s.course)?.course;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <nav aria-label={t('problem.breadcrumb')} className="mb-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1.5 text-content-secondary">
          <li>
            <Link to="/" className="transition-colors hover:text-content">
              {t('nav.home')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li>
            <Link to={COURSES_INDEX_PATH} className="transition-colors hover:text-content">
              {t('nav.courses')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" className="text-content">
            {career.title}
          </li>
        </ol>
      </nav>

      <header className="card mb-8 border-brand-500/30 bg-gradient-to-br from-brand-500/10 to-transparent p-6 sm:p-8">
        <div className="mb-3 flex items-center gap-2 text-brand-600">
          <Briefcase className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-wide">{t('courses.career.label')}</span>
        </div>
        <h1 className="text-2xl font-bold sm:text-3xl">{career.title}</h1>
        <p className="mt-3 max-w-3xl text-content-secondary">{career.shortDescription}</p>

        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-content-secondary">
          <span className="inline-flex items-center gap-1.5">
            <Route className="h-4 w-4" />
            {t('courses.career.steps', { count: career.stepCount })}
          </span>
          {career.lessonCount > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <PlayCircle className="h-4 w-4" />
              {t('courses.lessons', { count: career.lessonCount })}
            </span>
          )}
          {career.totalDurationSec > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {formatTotalDuration(career.totalDurationSec, t)}
            </span>
          )}
          {career.salaryText && (
            <span>
              {t('courses.career.salary')}: <span className="font-semibold text-content">{career.salaryText}</span>
            </span>
          )}
        </div>

        {firstCourse && (
          <Link
            to={coursePath(firstCourse.slug)}
            className="btn btn-primary mt-6 inline-flex gap-2 px-6 py-3 text-sm font-semibold"
          >
            <PlayCircle className="h-4 w-4" />
            {t('courses.career.start')}
          </Link>
        )}

        <PurchaseBox
          kind="career"
          title={career.title}
          priceUzs={career.priceUzs}
          owned={hasCareer(career.slug)}
          className="mt-6 border-t border-brand-500/20 pt-6"
        />
      </header>

      <section className="mb-10">
        <h2 className="mb-5 text-xl font-bold">{t('courses.career.path')}</h2>
        <ol className="relative space-y-4 border-l-2 border-line pl-6 sm:pl-8">
          {career.steps.map((s) => (
            <StepItem key={s.position} step={s} doneLessons={s.course ? done[s.course.slug] : undefined} />
          ))}
        </ol>
      </section>

      {career.descriptionMd && (
        <section>
          <h2 className="mb-3 text-xl font-bold">{t('courses.career.about')}</h2>
          <MarkdownLite text={career.descriptionMd} />
        </section>
      )}
    </div>
  );
}

function StepItem({ step, doneLessons }: { step: CareerStep; doneLessons?: number }) {
  const { t } = useTranslation();
  const course = step.available ? step.course : undefined;
  const finished = course !== undefined && doneLessons !== undefined && doneLessons >= course.lessonCount;

  const marker = (
    <span
      className={cn(
        // Chiziq markazida: pl (1.5/2rem) + yarim diametr (0.75/1rem) + yarim chiziq (1px)
        'absolute -left-[calc(2.25rem+1px)] top-4 flex h-6 w-6 items-center justify-center rounded-full border-2 text-xs font-bold sm:-left-[calc(3rem+1px)] sm:h-8 sm:w-8',
        finished
          ? 'border-[var(--easy)] bg-[var(--easy)] text-white'
          : course
            ? 'border-brand-600 bg-elevated text-brand-600'
            : 'border-line bg-surface text-content-muted',
      )}
      aria-hidden
    >
      {finished ? <CheckCircle2 className="h-4 w-4" /> : step.position}
    </span>
  );

  if (!course) {
    return (
      <li className="relative">
        {marker}
        <div className="card border-dashed p-5 opacity-80">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-content-secondary">{step.title}</h3>
            <span className="inline-flex items-center gap-1 rounded bg-surface px-2 py-0.5 text-xs font-semibold text-content-muted">
              <Hourglass className="h-3 w-3" /> {t('courses.career.soon')}
            </span>
          </div>
          {step.description && <p className="mt-1.5 text-sm text-content-muted">{step.description}</p>}
          <p className="mt-2 text-xs text-content-muted">{t('courses.career.comingSoonHint')}</p>
        </div>
      </li>
    );
  }

  return (
    <li className="relative">
      {marker}
      <Link to={coursePath(course.slug)} className="card card-hover group block p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold transition-colors group-hover:text-brand-600">{step.title}</h3>
          <span className="tag">{t(`courses.level.${course.level}`)}</span>
        </div>
        <p className="mt-1.5 text-sm text-content-secondary">{step.description ?? course.shortDescription}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-muted">
          <span className="inline-flex items-center gap-1">
            <PlayCircle className="h-3.5 w-3.5" />
            {t('courses.lessons', { count: course.lessonCount })}
          </span>
          {course.totalDurationSec > 0 && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {formatTotalDuration(course.totalDurationSec, t)}
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-1 font-medium text-brand-600">
            {t('courses.career.open')} <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
        {doneLessons !== undefined && (
          <div className="mt-3">
            <p className="mb-1 text-xs text-content-muted">
              {t('courses.career.stepProgress', { done: doneLessons, total: course.lessonCount })}
            </p>
            <ProgressBar value={doneLessons} max={course.lessonCount} />
          </div>
        )}
      </Link>
    </li>
  );
}
